<template>
    <FilterBase
        :title
        class="filter-base"
    >
        <div class="shape-filter">
            <ThLink
                v-for="shape in shapes"
                :key="shape.systemCode"
                :class="getClassFilter(shape)"
                :data-qa-code="shape.systemCode"
                :to="toggleShape(shape.systemCode)"
                class="filter"
                @click="toggleShape2(shape)"
            >
                <div
                    v-if="shape?.icon?.url"
                    class="filter-icon-wrapper"
                >
                    <img
                        :alt="shape.translation?.name"
                        :src="shape.icon.url"
                        class="filter-icon"
                    />
                </div>

                {{ shape?.translation?.name }}
            </ThLink>
        </div>

        <div
            v-if="lockedTo"
            class="shape-locked-container"
        >
            <span class="shape-locked-text">
                {{ t('diamond_search.shapes_limited', shapes.length) }}
            </span>
        </div>

        <ThDialog
            :confirm-label="'Yes'"
            :is-open="!!pendingShapeConfirmation"
            :prompt="`Change your ring shape to ${pendingShapeConfirmation?.translation?.name}?`"
            @close="pendingShapeConfirmation = null"
            @confirm="confirmRingShape"
        />
    </FilterBase>
</template>

<script
    generic="
        TCode extends string,
        TShape extends {
            systemCode: TCode
            icon?: { url: string } | null
            translation?: { name: string }
        }
    "
    lang="ts"
    setup
>
import { type Ref, ref, toValue } from 'vue'
import { useI18n } from 'vue-i18n'

import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import { getCatalogueEngagementRingFilterInput } from '@/domain/product/ring/engagement.ts'

import FilterBase from '@/components/ui/filter/filters/layout/FilterBase.vue'
import ThLink from '@/components/ui/buttons/ThLink.vue'
import ThDialog from '@/components/layout/modal/ThDialog.vue'
import { useRingBuilder } from '@/plugins/ring-builder.ts'

const { t } = useI18n()

const props = defineProps<{
    selectedShape: TCode | undefined
    shapes: TShape[]
    lockedTo?: TShape
    getRoute: (shape?: TCode) => ButtonRouteRelative
    title?: string
}>()

const pendingShapeConfirmation: Ref<TShape | null> = ref(null)

const { addRing, mountFragment, signatureStone } = useRingBuilder()

const toggleShape = (shape: TCode) => {
    if (props.lockedTo) {
        return undefined
    }

    if (props.selectedShape === shape) {
        return props.getRoute()
    }

    return props.getRoute(shape)
}

const toggleShape2 = (shape: TShape) => {
    if (!props.lockedTo || props.lockedTo.systemCode === shape.systemCode) {
        return
    }

    pendingShapeConfirmation.value = shape
}

const confirmRingShape = async () => {
    const pendingShapeConfirmationVal = toValue(pendingShapeConfirmation)
    const mountFragmentVal = toValue(mountFragment)

    if (!pendingShapeConfirmationVal || !mountFragmentVal) {
        return
    }

    const newRing = mountFragmentVal.ring?.variationsByCentreStoneShape.find(
        (variation) =>
            variation.centreStoneConstraints.shape.systemCode ===
            pendingShapeConfirmationVal.systemCode
    )

    if (newRing) {
        await addRing(
            getCatalogueEngagementRingFilterInput(newRing),
            signatureStone.value
        )
    }
}

const getClassFilter = (value: TShape) => ({
    'selected': props.selectedShape === value.systemCode,
})
</script>

<style lang="scss" scoped>
.filter-base {
    margin-bottom: 20px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    min-height: 100px;

    .shape-filter {
        display: flex;
        overflow-x: auto;
        overflow-y: hidden;
        padding-left: 24px;
        padding-right: 10px;
        gap: 20px;

        @include min-width(1024) {
            padding-left: 10px;
            overflow-x: unset;
        }

        .filter {
            position: relative;
            color: $slate;
            cursor: pointer;
            width: 54px;
            margin: 0 8px;
            display: flex;
            flex-direction: column;
            align-items: center;

            font-family: $effra-font-stack;
            font-weight: 400;
            font-size: 0.675rem;
            line-height: 1em;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            text-align: center;

            &.selected {
                .filter-icon-wrapper {
                    border-color: $blue;
                }
            }

            .filter-icon-wrapper {
                margin-bottom: 5px;
                width: 74px;
                height: 74px;
                display: flex;
                align-items: center;
                justify-content: center;
                border: 1px solid $grey-200;
                border-radius: 8px;

                .filter-icon {
                    width: 38px;
                    height: 38px;
                }
            }
        }
    }

    .shape-locked-container {
        display: flex;
        width: 100%;
        justify-content: center;
        padding: 20px 10px 0;

        .shape-locked-text {
            font-size: 0.875rem;
            padding: 10px 20px;
            background: rgb(252, 246, 234);
            align-self: flex-start;
            border-radius: 28px;
            line-height: 18px;
            display: flex;
            align-items: center;
            text-align: center;
        }
    }
}
</style>
